import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { client } from '../../utils/client'
import { loginUser, loginWithLineSessionToken } from '../auth/authSlice'

// Actions - used with useDispatch
export const userSlice = createSlice({
  name: 'user',
  initialState: {
    profile: null,
    getProfile: {
      status: null,
      error: null,
    },
    updateProfile: {
      status: null,
      error: null,
    },
    requestLineAuthCode: {
      status: null,
      error: null,
    },
    unlinkLine: {
      status: null,
      error: null,
    },
    lineNotificationsMuted: {
      status: null,
      error: null,
    },
    updatePhone: {
      status: null,
      error: null,
    },
    verifyPhone: {
      status: null,
      error: null,
    },
    checkInit: {
      status: null,
      error: null,
    },
    requestEmailOTP: {
      status: null,
      error: null,
    },
    verifyEmailOTP: {
      status: null,
      error: null,
    },
  },
  reducers: {
    clearStatus: (state) => {
      for (const key in state) {
        // Clear status and error
        if (state[key]?.status) {
          state[key].status = null
        }
        if (state[key]?.error) {
          state[key].error = null
        }
      }
    },
    clearUser: (state) => {
      // Clear user profile
      state.profile = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Clear stale profile when a new user logs in (handles user switch / re-login)
      .addCase(loginUser.fulfilled, (state) => {
        state.profile = null
      })
      .addCase(loginWithLineSessionToken.fulfilled, (state) => {
        state.profile = null
      })
      // getProfile
      .addCase(getProfile.pending, (state, action) => {
        state.getProfile.status = 'loading'
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.getProfile.status = 'succeeded'
        state.getProfile.error = null // Clear any previous errors

        // Store user info
        if (action.payload.data.success) {
          state.profile = action.payload.data.data
        }
      })
      .addCase(getProfile.rejected, (state, action) => {
        state.getProfile.status = 'failed'
        state.getProfile.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }

        state.user = null
      })
      // updateProfile
      .addCase(updateProfile.pending, (state, action) => {
        state.updateProfile.status = 'loading'
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.updateProfile.status = 'succeeded'
        state.updateProfile.error = null // Clear any previous errors

        // Reflect updates
        if (action.payload.data.success) {
          state.profile = action.payload.data.data
        }
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.updateProfile.status = 'failed'
        state.updateProfile.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }
      })
      // requestLineAuthCode
      .addCase(requestLineAuthCode.pending, (state, action) => {
        state.requestLineAuthCode.status = 'loading'
      })
      .addCase(requestLineAuthCode.fulfilled, (state, action) => {
        state.requestLineAuthCode.status = 'succeeded'
        state.requestLineAuthCode.error = null // Clear any previous errors

        // Reflect updates
      })
      .addCase(requestLineAuthCode.rejected, (state, action) => {
        state.requestLineAuthCode.status = 'failed'
        state.requestLineAuthCode.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }
      })
      // unlinkLine
      .addCase(unlinkLine.pending, (state, action) => {
        state.unlinkLine.status = 'loading'
      })
      .addCase(unlinkLine.fulfilled, (state, action) => {
        state.unlinkLine.status = 'succeeded'
        state.unlinkLine.error = null // Clear any previous errors

        // Reflect updates
      })
      .addCase(unlinkLine.rejected, (state, action) => {
        state.unlinkLine.status = 'failed'
        state.unlinkLine.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }
      })
      .addCase(setLineNotificationsMuted.pending, (state) => {
        state.lineNotificationsMuted.status = 'loading'
      })
      .addCase(setLineNotificationsMuted.fulfilled, (state, action) => {
        state.lineNotificationsMuted.status = 'succeeded'
        state.lineNotificationsMuted.error = null
        if (action.payload.data?.success) {
          state.profile = action.payload.data.data
        }
      })
      .addCase(setLineNotificationsMuted.rejected, (state, action) => {
        state.lineNotificationsMuted.status = 'failed'
        state.lineNotificationsMuted.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
      })
      // updatePhone
      .addCase(updatePhone.pending, (state) => {
        state.updatePhone.status = 'loading'
      })
      .addCase(updatePhone.fulfilled, (state, action) => {
        state.updatePhone.status = 'succeeded'
        state.updatePhone.error = null
      })
      .addCase(updatePhone.rejected, (state, action) => {
        state.updatePhone.status = 'failed'
        state.updatePhone.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
      })
      // verifyPhone
      .addCase(verifyPhone.pending, (state) => {
        state.verifyPhone.status = 'loading'
      })
      .addCase(verifyPhone.fulfilled, (state, action) => {
        state.verifyPhone.status = 'succeeded'
        state.verifyPhone.error = null
      })
      .addCase(verifyPhone.rejected, (state, action) => {
        state.verifyPhone.status = 'failed'
        state.verifyPhone.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
      })
      // Add checkInit reducers
      .addCase(checkInit.pending, (state) => {
        state.checkInit.status = 'loading'
      })
      .addCase(checkInit.fulfilled, (state, action) => {
        state.checkInit.status = 'succeeded'
        state.checkInit.error = null
      })
      .addCase(checkInit.rejected, (state, action) => {
        state.checkInit.status = 'failed'
        state.checkInit.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
      })
      // requestEmailOTP
      .addCase(requestEmailOTP.pending, (state) => {
        state.requestEmailOTP.status = 'loading'
      })
      .addCase(requestEmailOTP.fulfilled, (state) => {
        state.requestEmailOTP.status = 'succeeded'
        state.requestEmailOTP.error = null
      })
      .addCase(requestEmailOTP.rejected, (state, action) => {
        state.requestEmailOTP.status = 'failed'
        state.requestEmailOTP.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
      })
      // verifyEmailOTP
      .addCase(verifyEmailOTP.pending, (state) => {
        state.verifyEmailOTP.status = 'loading'
      })
      .addCase(verifyEmailOTP.fulfilled, (state) => {
        state.verifyEmailOTP.status = 'succeeded'
        state.verifyEmailOTP.error = null
      })
      .addCase(verifyEmailOTP.rejected, (state, action) => {
        state.verifyEmailOTP.status = 'failed'
        state.verifyEmailOTP.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
      })
  },
})
export const { clearStatus, clearUser } = userSlice.actions

// Async API (Thunks) - used with useDispatch
export const getProfile = createAsyncThunk('user/getProfile', async (_, { rejectWithValue }) => {
  try {
    const response = await client.get('/shop/profile')

    return response
  } catch (error) {
    // Handle the error gracefully and return it as part of the payload
    if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
      return rejectWithValue({
        status: 503,
        message: error.message,
      })
    }
    // API response error
    return rejectWithValue({
      status: error.response?.status || 500,
      message: error.response?.data?.message || error.message,
    })
  }
})

export const updateProfile = createAsyncThunk(
  'user/updateProfile',
  async (profile, { dispatch, rejectWithValue }) => {
    try {
      if (profile) {
        const rawPhoto = profile?.profilePhoto
        const profilePhoto = Array.isArray(rawPhoto)
          ? rawPhoto.length > 0
            ? rawPhoto[0]
            : ''
          : rawPhoto
        const payload = {
          ...profile,
          profilePhoto,
        }
        const url = `/shop/profile`
        const response = await client.put(url, payload)

        return response
      } else {
        return rejectWithValue('No updates parameters')
      }
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// Line
// Request auth code
export const requestLineAuthCode = createAsyncThunk(
  'user/requestLineAuthCode',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const url = `/line/request-authcode`
      const response = await client.post(url)

      return response
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// Unlink Line account
export const unlinkLine = createAsyncThunk(
  'user/unlinkLine',
  async (lineUserId, { rejectWithValue }) => {
    try {
      const url = `/line/unlink`
      const response = await client.post(url, lineUserId ? { lineUserId } : {})

      return response
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const setLineNotificationsMuted = createAsyncThunk(
  'user/setLineNotificationsMuted',
  async ({ lineUserId, muted }, { rejectWithValue }) => {
    try {
      const response = await client.post('/line/notifications-muted', {
        ...(lineUserId ? { lineUserId } : {}),
        muted,
      })
      return response
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({ status: 503, message: error.message })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// New async thunks
export const updatePhone = createAsyncThunk(
  'user/updatePhone',
  async (phone, { rejectWithValue }) => {
    try {
      const response = await client.put('/user/phone', {
        phone,
        countryCode: 'TH',
      })
      return response
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const verifyPhone = createAsyncThunk(
  'user/verifyPhone',
  async ({ phone, refCode, otpCode }, { rejectWithValue }) => {
    try {
      const response = await client.post('/user/phone/verify', {
        phone,
        refCode,
        otpCode,
      })
      return response
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const requestEmailOTP = createAsyncThunk(
  'user/requestEmailOTP',
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await client.post('/user/request-email-otp', { email })
      return response
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({ status: 503, message: error.message })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const verifyEmailOTP = createAsyncThunk(
  'user/verifyEmailOTP',
  async ({ email, otpCode, refCode }, { rejectWithValue }) => {
    try {
      const response = await client.post('/user/verify-email-otp', { email, otpCode, refCode })
      return response
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({ status: 503, message: error.message })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// Add new async thunk for check-init
export const checkInit = createAsyncThunk('user/checkInit', async (_, { rejectWithValue }) => {
  try {
    const response = await client.get('/user/check-init')
    return response
  } catch (error) {
    if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
      return rejectWithValue({
        status: 503,
        message: error.message,
      })
    }
    return rejectWithValue({
      status: error.response?.status || 500,
      message: error.response?.data?.message || error.message,
    })
  }
})

// Selector (Read data) - used with useSelector
export const selectProfile = (state) => state.user.profile
export const selectGetProfileStatus = (state) => state.user.getProfile.status
export const selectGetProfileError = (state) => state.user.getProfile.error
export const selectUpdateProfileStatus = (state) => state.user.updateProfile.status
export const selectUpdateProfileError = (state) => state.user.updateProfile.error
export const selectRequestLineAuthCodeStatus = (state) => state.user.requestLineAuthCode.status
export const selectRequestLineAuthCodeError = (state) => state.user.requestLineAuthCode.error
export const selectUnlinkLineStatus = (state) => state.user.unlinkLine.status
export const selectUnlinkLineError = (state) => state.user.unlinkLine.error
export const selectLineNotificationsMutedStatus = (state) =>
  state.user.lineNotificationsMuted.status
export const selectLineNotificationsMutedError = (state) => state.user.lineNotificationsMuted.error
export const selectUpdatePhoneStatus = (state) => state.user.updatePhone.status
export const selectUpdatePhoneError = (state) => state.user.updatePhone.error
export const selectVerifyPhoneStatus = (state) => state.user.verifyPhone.status
export const selectVerifyPhoneError = (state) => state.user.verifyPhone.error
export const selectCheckInitStatus = (state) => state.user.checkInit.status
export const selectCheckInitError = (state) => state.user.checkInit.error
export const selectRequestEmailOTPStatus = (state) => state.user.requestEmailOTP.status
export const selectVerifyEmailOTPStatus = (state) => state.user.verifyEmailOTP.status

export default userSlice.reducer
